import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, collection, getDocs, query, where, orderBy, limit, writeBatch, runTransaction,
} from 'firebase/firestore';
import Nav from './Nav';
import { getOpenReports, reviewReport, deleteReport } from './reports';
import { listenAdminConversations, listenMessages, adminSendMessage, adminCloseConversation, markConversationRead } from './messages';

function AdminPanel({ currentUser, currentUserProfile }) {
  const isAdmin = currentUserProfile?.isAdmin === true || currentUserProfile?.username === 'corey';

  const [search, setSearch]     = useState('');
  const [target, setTarget]     = useState(null); // { uid, ...userData }
  const [status, setStatus]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [reports, setReports]   = useState([]);
  const [feedback, setFeedback] = useState([]);

  const loadReports = useCallback(async () => {
    if (!isAdmin) return;
    try { setReports(await getOpenReports()); } catch (e) { console.error('Load reports failed:', e); }
  }, [isAdmin]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const loadFeedback = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const snap = await getDocs(query(collection(db, 'feedback'), orderBy('createdAt', 'desc'), limit(100)));
      setFeedback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('Load feedback failed:', e); }
  }, [isAdmin]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const [flagged, setFlagged] = useState([]);
  const [flaggedImg, setFlaggedImg] = useState({}); // id -> data URL (revealed on demand)
  const loadFlagged = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const snap = await getDocs(query(collection(db, 'flaggedPhotos'), orderBy('createdAt', 'desc'), limit(50)));
      setFlagged(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('Load flagged failed:', e); }
  }, [isAdmin]);
  useEffect(() => { loadFlagged(); }, [loadFlagged]);

  const revealFlagged = async (item) => {
    try {
      const fn = httpsCallable(functions, 'getFlaggedPhotoData');
      const res = await fn({ path: item.storagePath });
      setFlaggedImg(m => ({ ...m, [item.id]: res.data.dataUrl }));
    } catch (e) { console.error('Reveal failed:', e); }
  };
  const deleteFlagged = async (item) => {
    try {
      const fn = httpsCallable(functions, 'deleteFlaggedPhoto');
      await fn({ id: item.id, path: item.storagePath });
      setFlagged(fs => fs.filter(f => f.id !== item.id));
    } catch (e) { console.error('Delete flagged failed:', e); }
  };
  const allowFlagged = async (item) => {
    if (!window.confirm(`Approve @${item.username || item.uid}'s photo and put it back on their profile?`)) return;
    try {
      const fn = httpsCallable(functions, 'restoreFlaggedPhoto');
      await fn({ id: item.id, path: item.storagePath, uid: item.uid });
      setFlagged(fs => fs.filter(f => f.id !== item.id));
    } catch (e) { console.error('Restore flagged failed:', e); }
  };
  const [lightbox, setLightbox] = useState(null); // enlarged flagged image data URL

  const [verifications, setVerifications] = useState([]);
  const loadVerifications = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const snap = await getDocs(query(collection(db, 'verificationRequests'), where('status', '==', 'pending')));
      setVerifications(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    } catch (e) { console.error('Load verifications failed:', e); }
  }, [isAdmin]);
  useEffect(() => { loadVerifications(); }, [loadVerifications]);

  const handleReviewVerification = async (uid, approve) => {
    try {
      await httpsCallable(functions, 'reviewVerification')({ uid, approve });
      setVerifications(vs => vs.filter(v => v.uid !== uid));
    } catch (e) { console.error('Review verification failed:', e); }
  };

  // Proactively verify an account by username (no request needed).
  const [proVerifyInput, setProVerifyInput] = useState('');
  const [proVerifyMsg, setProVerifyMsg] = useState('');
  const handleProactiveVerify = async () => {
    const uname = proVerifyInput.trim().toLowerCase().replace(/^@/, '');
    if (!uname) return;
    setProVerifyMsg('Working…');
    try {
      const snap = await getDoc(doc(db, 'usernames', uname));
      if (!snap.exists()) { setProVerifyMsg(`No account @${uname}.`); return; }
      await httpsCallable(functions, 'reviewVerification')({ uid: snap.data().uid, approve: true });
      setProVerifyMsg(`Verified @${uname}.`);
      setProVerifyInput('');
      loadVerifications();
    } catch (e) { console.error('proactive verify failed', e); setProVerifyMsg('Failed — see console.'); }
  };

  // Admin messaging
  const [conversations, setConversations] = useState([]);
  const [activeUid, setActiveUid] = useState(null);
  const [convMsgs, setConvMsgs] = useState([]);
  const [msgDraft, setMsgDraft] = useState('');
  const [msgBusy, setMsgBusy] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    return listenAdminConversations(setConversations);
  }, [isAdmin]);
  useEffect(() => {
    if (!activeUid) { setConvMsgs([]); return; }
    const unsub = listenMessages(activeUid, setConvMsgs);
    markConversationRead(activeUid).catch(() => {});
    return unsub;
  }, [activeUid]);
  const sendAdminMsg = async () => {
    if (!activeUid || !msgDraft.trim() || msgBusy) return;
    setMsgBusy(true);
    try { await adminSendMessage(activeUid, msgDraft.trim()); setMsgDraft(''); }
    catch (e) { console.error('admin send failed', e); }
    setMsgBusy(false);
  };
  const closeConv = async (uid) => {
    if (!window.confirm('Close this conversation? The user will no longer see the inbox until you message them again.')) return;
    try { await adminCloseConversation(uid); } catch (e) { console.error('close failed', e); }
  };
  // Start (or jump to) a conversation with the currently searched user.
  const messageTarget = () => { if (target?.uid) { setActiveUid(target.uid); document.getElementById('admin-messages')?.scrollIntoView({ behavior: 'smooth' }); } };

  // Override email verification for a stuck account (verification email never landed).
  const verifyTargetEmail = async () => {
    if (!target?.uid) return;
    if (!window.confirm(`Mark @${target.username}'s email as verified? Use this when their verification email won't arrive.`)) return;
    setLoading(true);
    try { await httpsCallable(functions, 'adminVerifyEmail')({ uid: target.uid }); setStatus('Email marked verified. They can sign in now.'); }
    catch (e) { console.error('verify email failed', e); setStatus('Failed to verify email.'); }
    setLoading(false);
  };

  const [recomputeMsg, setRecomputeMsg] = useState('');
  const handleRecomputeCounts = async () => {
    setRecomputeMsg('Working…');
    try {
      const r = await httpsCallable(functions, 'recomputeConnectionCounts')();
      setRecomputeMsg(`Updated ${r.data.updated} profiles.`);
    } catch (e) { console.error('recompute failed', e); setRecomputeMsg('Failed — see console.'); }
  };

  // Verify a stuck account by EMAIL (unverified users have no username/profile yet).
  const [verifyEmailInput, setVerifyEmailInput] = useState('');
  const [verifyEmailMsg, setVerifyEmailMsg] = useState('');
  const handleVerifyByEmail = async () => {
    const email = verifyEmailInput.trim();
    if (!email) return;
    setVerifyEmailMsg('Working…');
    try {
      await httpsCallable(functions, 'adminVerifyEmail')({ email });
      setVerifyEmailMsg(`Verified ${email}. They can sign in now.`);
      setVerifyEmailInput('');
    } catch (e) {
      console.error('verify by email failed', e);
      setVerifyEmailMsg((e?.message || '').includes('No account') ? 'No account with that email.' : 'Failed — see console.');
    }
  };

  const handleReviewReport = async (id, outcome) => {
    await reviewReport(id, outcome);
    setReports(rs => rs.filter(r => r.id !== id));
  };
  const handleDeleteReport = async (id) => {
    await deleteReport(id);
    setReports(rs => rs.filter(r => r.id !== id));
  };

  const handleResolveFeedback = async (id) => {
    await updateDoc(doc(db, 'feedback', id), { status: 'resolved' });
    setFeedback(fs => fs.map(f => f.id === id ? { ...f, status: 'resolved' } : f));
  };
  const handleDeleteFeedback = async (id) => {
    await deleteDoc(doc(db, 'feedback', id));
    setFeedback(fs => fs.filter(f => f.id !== id));
  };

  const fmtDate = (ts) => {
    try { return ts?.toDate ? ts.toDate().toLocaleString() : ''; } catch { return ''; }
  };

  if (!currentUser || !isAdmin) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, fontWeight: 900, color: 'var(--accent)', marginBottom: 12 }}>403</div>
          <p style={{ color: 'var(--muted)' }}>You don't have access to this page.</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: 16 }}>Go Home</Link>
        </div>
      </div>
    );
  }

  const flash = (msg, good = true) => {
    setStatus({ msg, good });
    setTimeout(() => setStatus(''), 3000);
  };

  const handleBootstrap = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { isAdmin: true, verified: true });
      flash('Admin bootstrapped. Refresh to see changes.');
      setBootstrapped(true);
    } catch (e) {
      flash('Failed: ' + e.message, false);
    }
    setLoading(false);
  };

  const handleBootstrapUserNumber = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const counterRef = doc(db, 'meta', 'userCounter');
      const userRef = doc(db, 'users', currentUser.uid);
      await runTransaction(db, async (tx) => {
        // All reads first
        const counterSnap = await tx.get(counterRef);
        const userSnap = await tx.get(userRef);
        // Then all writes
        if (!counterSnap.exists()) {
          tx.set(counterRef, { count: 1 });
        } else if (counterSnap.data().count < 1) {
          tx.update(counterRef, { count: 1 });
        }
        if (userSnap.exists() && !userSnap.data().userNumber) {
          tx.update(userRef, { userNumber: 1 });
        }
      });
      flash('User #1 assigned to your account.');
    } catch (e) {
      flash('Failed: ' + e.message, false);
    }
    setLoading(false);
  };

  const backfillConnections = async () => {
    if (!window.confirm('Create connection records from every existing accepted Top 8 tile?')) return;
    setLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const users = usersSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
      const uidByUsername = {};
      users.forEach(u => { if (u.username) uidByUsername[u.username] = u.uid; });
      const seen = new Set();
      let created = 0;
      for (const u of users) {
        for (const t of (u.top8 || [])) {
          if (t.connectionStatus !== 'accepted' || !t.yt8Username) continue;
          const otherUid = uidByUsername[t.yt8Username];
          if (!otherUid || otherUid === u.uid) continue;
          const id = [u.uid, otherUid].sort().join('__');
          if (seen.has(id)) continue;
          seen.add(id);
          await setDoc(doc(db, 'connections', id), {
            users: [u.uid, otherUid],
            requestedBy: u.uid,
            status: 'accepted',
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          created++;
        }
      }
      flash(`Backfilled ${created} connection(s).`);
    } catch (e) { flash('Failed: ' + e.message, false); }
    setLoading(false);
  };

  const handleSearch = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setTarget(null);
    try {
      const uSnap = await getDoc(doc(db, 'usernames', search.trim().toLowerCase()));
      if (!uSnap.exists()) { flash('Username not found.', false); setLoading(false); return; }
      const uid = uSnap.data().uid;
      const pSnap = await getDoc(doc(db, 'users', uid));
      if (!pSnap.exists()) { flash('User document not found.', false); setLoading(false); return; }
      // Email now lives in the owner/admin-only private doc, not the public profile.
      let email = '';
      try {
        const privSnap = await getDoc(doc(db, 'users', uid, 'private', 'data'));
        if (privSnap.exists()) email = privSnap.data().email || '';
      } catch { /* admin-only read; ignore if absent */ }
      setTarget({ uid, ...pSnap.data(), email });
    } catch (e) {
      flash('Error: ' + e.message, false);
    }
    setLoading(false);
  };

  const refreshTarget = async () => {
    if (!target) return;
    const pSnap = await getDoc(doc(db, 'users', target.uid));
    if (pSnap.exists()) setTarget({ uid: target.uid, ...pSnap.data() });
  };

  const setVerified = async (val) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', target.uid), { verified: val });
      await refreshTarget();
      flash(val ? 'Verified status granted.' : 'Verified status revoked.');
    } catch (e) { flash('Failed: ' + e.message, false); }
    setLoading(false);
  };

  const setAdminFlag = async (val) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', target.uid), { isAdmin: val });
      await refreshTarget();
      flash(val ? 'Admin granted.' : 'Admin revoked.');
    } catch (e) { flash('Failed: ' + e.message, false); }
    setLoading(false);
  };

  const reclaimUsername = async () => {
    const newOwner = window.prompt(`Transfer @${target.username} to which username? (leave blank to reclaim and assign fallback slug)`);
    if (newOwner === null) return; // cancelled
    setLoading(true);
    try {
      if (newOwner.trim() === '') {
        // Reclaim slug — assign fallback u{userNumber} to the user
        const fallback = target.userNumber ? `u${target.userNumber}` : `u${target.uid.slice(0, 8)}`;
        const batch = writeBatch(db);
        batch.delete(doc(db, 'usernames', target.username));
        batch.set(doc(db, 'usernames', fallback), { uid: target.uid });
        batch.update(doc(db, 'users', target.uid), { username: fallback });
        await batch.commit();
        flash(`@${target.username} reclaimed. User reassigned to @${fallback}.`);
        setTarget(null);
      } else {
        // Transfer to existing user
        const destSnap = await getDoc(doc(db, 'usernames', newOwner.trim().toLowerCase()));
        if (!destSnap.exists()) { flash('Destination user not found.', false); setLoading(false); return; }
        const destUid = destSnap.data().uid;
        const batch = writeBatch(db);
        batch.set(doc(db, 'usernames', target.username), { uid: destUid });
        // Assign fallback to old user
        const fallback = target.userNumber ? `u${target.userNumber}` : `u${target.uid.slice(0, 8)}`;
        batch.set(doc(db, 'usernames', fallback), { uid: target.uid });
        batch.update(doc(db, 'users', target.uid), { username: fallback });
        batch.update(doc(db, 'users', destUid), { username: target.username });
        await batch.commit();
        flash(`@${target.username} transferred to @${newOwner}. Previous owner reassigned to @${fallback}.`);
        setTarget(null);
      }
    } catch (e) { flash('Failed: ' + e.message, false); }
    setLoading(false);
  };

  // Only names that collide with actual app routes are off-limits even to admins
  // (a username matching a route would make an unreachable profile). Brand names
  // like "yourtop8" are reserved from regular users but ASSIGNABLE by admin so we
  // can stand up official accounts.
  const ROUTE_RESERVED = [
    'create-profile', 'me', 'settings', 'admin', 'invite', 'privacy', 'terms',
    'legal', 'delete-account', 'people', 'feedback', 'help',
  ];

  const changeUsername = async () => {
    const oldUsername = target.username;
    const raw = window.prompt(`Change @${oldUsername} to which new username?`);
    if (raw === null) return;
    const next = raw.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!next) { flash('Invalid username.', false); return; }
    if (next === oldUsername) { flash('That is already their username.', false); return; }
    if (ROUTE_RESERVED.includes(next)) { flash(`@${next} collides with an app route and can't be used.`, false); return; }
    setLoading(true);
    try {
      const dest = await getDoc(doc(db, 'usernames', next));
      if (dest.exists()) { flash(`@${next} is already taken.`, false); setLoading(false); return; }
      const batch = writeBatch(db);
      batch.set(doc(db, 'usernames', next), { uid: target.uid });
      batch.delete(doc(db, 'usernames', oldUsername));
      batch.update(doc(db, 'users', target.uid), { username: next });
      await batch.commit();

      // Repoint any Top 8 tiles (across all users) that referenced the old handle.
      const usersSnap = await getDocs(collection(db, 'users'));
      let repointed = 0;
      for (const u of usersSnap.docs) {
        const top8 = u.data().top8 || [];
        let changed = false;
        const newTop8 = top8.map(t => {
          if (t.yt8Username !== oldUsername) return t;
          changed = true;
          return {
            ...t,
            yt8Username: next,
            primaryValue: t.primaryValue === oldUsername ? next : t.primaryValue,
            connections: (t.connections || []).map(c =>
              c.type === 'PROFILE' && c.value === oldUsername ? { ...c, value: next } : c
            ),
          };
        });
        if (changed) { await updateDoc(u.ref, { top8: newTop8 }); repointed++; }
      }

      flash(`Changed @${oldUsername} to @${next}.${repointed ? ` Repointed ${repointed} tile set(s).` : ''}`);
      setTarget({ ...target, username: next });
    } catch (e) { flash('Failed: ' + e.message, false); }
    setLoading(false);
  };

  const deleteAccount = async () => {
    if (!window.confirm(`Permanently delete @${target.username} (${target.displayName})? This cannot be undone.`)) return;
    setLoading(true);
    try {
      // Delete notifications subcollection
      const notifsSnap = await getDocs(collection(db, 'users', target.uid, 'notifications'));
      const batch = writeBatch(db);
      notifsSnap.docs.forEach(d => batch.delete(d.ref));
      // Delete user doc and username doc
      batch.delete(doc(db, 'users', target.uid));
      batch.delete(doc(db, 'usernames', target.username));
      await batch.commit();
      flash(`@${target.username} deleted.`);
      setTarget(null);
    } catch (e) { flash('Failed: ' + e.message, false); }
    setLoading(false);
  };

  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <div className="wrap" style={{ paddingTop: 32 }}>

        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Admin Panel</h1>
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: 14 }}>Manage accounts, verification, and usernames.</p>
        </div>

        {status && (
          <div style={{
            padding: '10px 16px', borderRadius: 'var(--radius)',
            background: status.good ? 'rgba(99,202,99,0.12)' : 'rgba(255,80,80,0.12)',
            border: `1px solid ${status.good ? 'rgba(99,202,99,0.3)' : 'rgba(255,80,80,0.3)'}`,
            color: status.good ? '#7ddb7d' : '#ff6b6b',
            fontSize: 14, marginBottom: 20,
          }}>
            {status.msg}
          </div>
        )}

        {/* Tools */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>Tools</strong>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px' }}>
            Create connection records from accepted Top 8 tiles (run once to migrate existing connections).
          </p>
          <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={backfillConnections} disabled={loading}>
            Backfill connections
          </button>
        </div>

        {/* Maintenance */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <strong>Maintenance</strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={handleRecomputeCounts}>
              Recompute connection counts
            </button>
            {recomputeMsg && <span style={{ fontSize: 13, color: 'var(--muted)' }}>{recomputeMsg}</span>}
          </div>

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Verify a stuck account by email</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
              For testers whose verification email never arrived. They have no username yet, so look them up by the email they signed up with.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                className="input"
                type="email"
                value={verifyEmailInput}
                onChange={e => { setVerifyEmailInput(e.target.value); setVerifyEmailMsg(''); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyByEmail(); } }}
                placeholder="tester@example.com"
                style={{ flex: '1 1 240px', minWidth: 0 }}
              />
              <button className="btn btn-sm btn-primary" onClick={handleVerifyByEmail}>Verify email</button>
            </div>
            {verifyEmailMsg && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>{verifyEmailMsg}</div>}
          </div>
        </div>

        {/* Verification requests */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Verification requests ({verifications.length})</strong>
            <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={loadVerifications}>
              Refresh
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 14px' }}>
            <input
              className="input"
              value={proVerifyInput}
              onChange={e => { setProVerifyInput(e.target.value); setProVerifyMsg(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleProactiveVerify(); } }}
              placeholder="@username to verify directly"
              style={{ flex: '1 1 220px', minWidth: 0 }}
            />
            <button className="btn btn-sm btn-primary" onClick={handleProactiveVerify}>Verify account</button>
            {proVerifyMsg && <span style={{ fontSize: 13, color: 'var(--muted)' }}>{proVerifyMsg}</span>}
          </div>
          {verifications.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '8px 0 0' }}>No pending requests.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {verifications.map(v => (
                <div key={v.uid} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 13 }}>
                    <Link to={`/${v.username}`} style={{ color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>@{v.username || v.uid}</Link>
                    <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase' }}>{v.category}</span>
                  </div>
                  {v.website && <div style={{ fontSize: 13, margin: '6px 0 0' }}>
                    <a href={/^https?:\/\//.test(v.website) ? v.website : `https://${v.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{v.website}</a>
                  </div>}
                  {v.notes && <p style={{ fontSize: 13, margin: '6px 0 10px', whiteSpace: 'pre-wrap', color: 'var(--muted)' }}>{v.notes}</p>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => handleReviewVerification(v.uid, true)}>Approve</button>
                    <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => handleReviewVerification(v.uid, false)}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin messaging */}
        <div id="admin-messages" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <strong>Messages</strong>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 12px' }}>
            Message a user directly. Search a user above and hit “Message”, or pick a conversation. The user only sees an inbox while a conversation is open.
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {/* Conversation list */}
            <div style={{ flex: '1 1 200px', minWidth: 180, maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {conversations.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>No conversations.</p>
              ) : conversations.map(c => (
                <button
                  key={c.uid}
                  onClick={() => setActiveUid(c.uid)}
                  style={{
                    textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font)',
                    border: `1px solid ${activeUid === c.uid ? 'var(--accent)' : 'var(--border)'}`,
                    background: activeUid === c.uid ? 'var(--card-2)' : 'transparent', color: 'var(--text)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
                    @{c.username || c.uid}
                    {c.unreadForAdmin && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--accent)' }} />}
                    {c.open ? null : <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>(closed)</span>}
                  </div>
                </button>
              ))}
            </div>

            {/* Active thread */}
            <div style={{ flex: '2 1 300px', minWidth: 240, border: '1px solid var(--border)', borderRadius: 10, display: 'flex', flexDirection: 'column', maxHeight: 360 }}>
              {!activeUid ? (
                <p style={{ fontSize: 13, color: 'var(--muted)', padding: 16, margin: 0 }}>Select or start a conversation.</p>
              ) : (
                <>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Link to={`/${conversations.find(c => c.uid === activeUid)?.username || ''}`} style={{ color: 'var(--accent)', fontWeight: 700, textDecoration: 'none', fontSize: 13 }}>
                      @{conversations.find(c => c.uid === activeUid)?.username || activeUid}
                    </Link>
                    <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => closeConv(activeUid)}>Close</button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {convMsgs.length === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--muted)' }}>No messages yet. Say hello.</p>
                    ) : convMsgs.map(m => (
                      <div key={m.id} style={{ alignSelf: m.from === 'admin' ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                        <div style={{
                          padding: '8px 11px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap',
                          background: m.from === 'admin' ? 'var(--accent)' : 'var(--card-2)',
                          color: m.from === 'admin' ? '#060610' : 'var(--text)',
                        }}>{m.body}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: 10, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                    <input
                      className="input"
                      value={msgDraft}
                      onChange={e => setMsgDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAdminMsg(); } }}
                      placeholder="Message…"
                      style={{ flex: 1 }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={sendAdminMsg} disabled={msgBusy || !msgDraft.trim()}>Send</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reports / takedown queue */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Reports ({reports.length})</strong>
            <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={loadReports}>
              Refresh
            </button>
          </div>
          {reports.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '8px 0 0' }}>No open reports.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {reports.map(r => (
                <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13 }}>
                      <Link to={`/${r.targetUsername}`} style={{ color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>@{r.targetUsername || r.targetUid}</Link>
                      {r.targetType && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase' }}>{r.targetType}</span>}
                      <span style={{ color: 'var(--muted)' }}> reported by @{r.reporterUsername || r.reporterUid}</span>
                    </div>
                  </div>
                  <p style={{ fontSize: 14, margin: '8px 0 10px', whiteSpace: 'pre-wrap' }}>{r.reason}</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => handleReviewReport(r.id, 'upheld')}>Uphold</button>
                    <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => handleReviewReport(r.id, 'dismissed')}>Dismiss</button>
                    <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => handleDeleteReport(r.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Feedback queue */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Feedback ({feedback.filter(f => f.status !== 'resolved').length} open)</strong>
            <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={loadFeedback}>
              Refresh
            </button>
          </div>
          {feedback.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '8px 0 0' }}>No feedback yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {feedback.map(f => (
                <div key={f.id} style={{
                  border: '1px solid var(--border)', borderRadius: 10, padding: 12,
                  opacity: f.status === 'resolved' ? 0.55 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--muted)' }}>
                    <span>
                      <strong style={{ color: 'var(--text)' }}>{f.fromUsername || 'anonymous'}</strong>
                      {f.contact ? ` · ${f.contact}` : ''}
                      {f.page ? ` · ${f.page}` : ''}
                    </span>
                    <span>{fmtDate(f.createdAt)}</span>
                  </div>
                  <p style={{ fontSize: 14, margin: '8px 0 10px', whiteSpace: 'pre-wrap' }}>{f.message}</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {f.status !== 'resolved' && (
                      <button className="btn btn-sm btn-primary" onClick={() => handleResolveFeedback(f.id)}>Mark resolved</button>
                    )}
                    <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => handleDeleteFeedback(f.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Flagged photos (NSFW moderation queue) */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong>Flagged photos ({flagged.length})</strong>
            <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={loadFlagged}>
              Refresh
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 4px' }}>
            Auto-removed by the NSFW check. Reveal to judge a false flag vs. real abuse. Higher counts = repeat attempts.
          </p>
          {flagged.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '8px 0 0' }}>Nothing flagged.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {flagged.map(f => (
                <div key={f.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, display: 'flex', gap: 12 }}>
                  <div style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', background: 'var(--card-2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {flaggedImg[f.id]
                      ? <img src={flaggedImg[f.id]} alt="flagged" title="Click to enlarge" onClick={() => setLightbox(flaggedImg[f.id])} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
                      : <button className="btn btn-sm" style={{ background: 'var(--card)', border: '1px solid var(--border)', fontSize: 11 }} onClick={() => revealFlagged(f)}>Reveal</button>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>
                      <Link to={`/${f.username}`} style={{ color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>@{f.username || f.uid}</Link>
                      {typeof f.flagCount === 'number' && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: f.flagCount > 1 ? '#ff6b6b' : 'var(--muted)' }}>
                          removal #{f.flagCount}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                      adult={f.adult} · racy={f.racy} · violence={f.violence}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-sm btn-primary" onClick={() => allowFlagged(f)}>Allow</button>
                      <button className="btn btn-sm" style={{ background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.3)', color: '#ff6b6b' }} onClick={() => deleteFlagged(f)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bootstrap section */}
        {(!currentUserProfile?.isAdmin && !bootstrapped) || !currentUserProfile?.userNumber ? (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
            <strong style={{ display: 'block', marginBottom: 6 }}>Bootstrap</strong>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px' }}>
              One-time setup actions for your account.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!currentUserProfile?.isAdmin && !bootstrapped && (
                <button className="btn btn-primary btn-sm" onClick={handleBootstrap} disabled={loading}>
                  Bootstrap Admin
                </button>
              )}
              {!currentUserProfile?.userNumber && (
                <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={handleBootstrapUserNumber} disabled={loading}>
                  Assign User #1 to @{currentUserProfile?.username}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {/* Search */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 24 }}>
          <strong style={{ display: 'block', marginBottom: 12 }}>Find User</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              style={{ flex: 1 }}
              placeholder="Username"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-primary btn-sm" onClick={handleSearch} disabled={loading || !search.trim()}>
              {loading ? <span className="spinner" style={{ width: 14, height: 14, borderTopColor: '#0d0d0d' }} /> : 'Search'}
            </button>
          </div>
        </div>

        {/* Target user card */}
        {target && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', background: 'var(--card-2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, color: 'var(--accent)' }}>
                {target.photoURL
                  ? <img src={target.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : target.displayName?.[0]?.toUpperCase()
                }
              </div>
              <div>
                <div style={{ fontWeight: 700 }}>{target.displayName}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>@{target.username}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{target.email}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  User #{target.userNumber ?? 'unassigned'}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {target.verified && (
                  <span style={{ background: '#f4b942', color: '#2a1800', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>Verified</span>
                )}
                {target.isAdmin && (
                  <span style={{ background: 'var(--accent)', color: '#060610', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>Admin</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {!target.verified
                ? <button className="btn btn-sm" style={{ background: '#f4b942', color: '#2a1800', border: 'none', fontWeight: 700 }} onClick={() => setVerified(true)} disabled={loading}>Grant Verified</button>
                : <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => setVerified(false)} disabled={loading}>Revoke Verified</button>
              }
              {!target.isAdmin
                ? <button className="btn btn-sm" style={{ background: 'var(--accent)', color: '#060610', border: 'none', fontWeight: 700 }} onClick={() => setAdminFlag(true)} disabled={loading}>Grant Admin</button>
                : target.uid !== currentUser.uid && (
                  <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={() => setAdminFlag(false)} disabled={loading}>Revoke Admin</button>
                )
              }
              <button className="btn btn-sm" style={{ background: 'var(--accent)', color: '#060610', border: 'none', fontWeight: 700 }} onClick={changeUsername} disabled={loading}>
                Change Username
              </button>
              <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={reclaimUsername} disabled={loading}>
                Reclaim @{target.username}
              </button>
              <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={messageTarget} disabled={loading}>
                Message
              </button>
              <button className="btn btn-sm" style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }} onClick={verifyTargetEmail} disabled={loading}>
                Verify email
              </button>
              {target.uid !== currentUser.uid && (
                <button className="btn btn-sm" style={{ background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.3)', color: '#ff6b6b' }} onClick={deleteAccount} disabled={loading}>
                  Delete Account
                </button>
              )}
            </div>
          </div>
        )}

      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}
        >
          <img src={lightbox} alt="flagged enlarged" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </>
  );
}

export default AdminPanel;
